import React, { FC, useCallback, useState } from 'react'
import text from 'styles/utility/text.module.scss'
import { FlexRow } from 'components/Flex'
import { TextButton } from 'components/Button'
import { Spacer } from 'components/Utility'
import { SettingsConfirmCard } from 'components/Card'
import { BreadType } from 'types/bread'
import { StockType } from 'types/stock'
import { ScheduleType } from 'types/schedule'
import { TemporaryOpenDate } from 'types/calendar'
import { getBreadShortName } from 'lib/helper'

type Props = {
  breadList: BreadType[]
  deletedStocks: StockType[]
  schedules: ScheduleType[]
  updatedSchedules: ScheduleType[]
  temporaryOpenDates: TemporaryOpenDate[]
  generatedDailyStocksByDefaultDayOfWeeks: TemporaryOpenDate[]
}

const SettingsConfirmCardList: FC<Props> = (props) => {
  const {
    breadList,
    deletedStocks,
    schedules,
    updatedSchedules,
    temporaryOpenDates,
    generatedDailyStocksByDefaultDayOfWeeks,
  } = props

  const [expandStatus, setIsExpandedAll] = useState<string>('expanded')

  const expandAllAccordion = useCallback(() => setIsExpandedAll('expanded'), [setIsExpandedAll])
  const foldAllAccordion = useCallback(() => setIsExpandedAll('folded'), [setIsExpandedAll])
  const resetExpandedStatus = useCallback(() => setIsExpandedAll(''), [setIsExpandedAll])

  return (
    <>
      <p className={text.font_bold}>{breadList.length}件の商品を表示中</p>
      <Spacer size={12} />
      <FlexRow justifyContent={'start'}>
        <TextButton
          disabled={expandStatus === 'expanded'}
          iconPath={expandStatus === 'expanded' ? '/icon/grey/plus.svg' : '/icon/blue/plus.svg'}
          label={'すべて開く'}
          onClick={expandAllAccordion}
        />
        <Spacer size={32} axis={'horizontal'} />
        <TextButton
          disabled={expandStatus === 'folded'}
          iconPath={expandStatus === 'folded' ? '/icon/grey/minus.svg' : '/icon/blue/minus.svg'}
          label={'すべてたたむ'}
          onClick={foldAllAccordion}
        />
      </FlexRow>
      <Spacer size={12} />
      {breadList.map((bread, index) => {
        if (!bread.bread_ref) return <React.Fragment key={index.toString()} />
        const dailySchedules = updatedSchedules.filter((schedule) => {
          return schedule.bread_ref.id === bread.bread_ref?.id && schedule.type === 'daily'
        })
        const prevDailySchedules = schedules.filter((schedule) => {
          return schedule.bread_ref.id === bread.bread_ref?.id && schedule.type === 'daily'
        })
        const preorderSchedules = updatedSchedules.filter((schedule) => {
          return schedule.bread_ref.id === bread.bread_ref?.id && schedule.type === 'preorder'
        })
        const prevPreorderSchedules = schedules.filter((schedule) => {
          return schedule.bread_ref.id === bread.bread_ref?.id && schedule.type === 'preorder'
        })
        const stockList = deletedStocks.reduce((prev, stock) => {
          if (
            stock.bread_ref.id === bread.bread_ref?.id &&
            prev.every((_stock) => _stock.date !== stock.date) // 重複している日付を除外
          ) {
            return [...prev, stock]
          } else {
            return [...prev]
          }
        }, [] as StockType[])

        const temporaryOpenDatesForThisBread = temporaryOpenDates.filter((temporaryOpenDate) => {
          return temporaryOpenDate.breadRef.id === bread.bread_ref?.id
        })

        const generatedDailyStocksByDefaultDayOfWeeksForThisBread = generatedDailyStocksByDefaultDayOfWeeks.filter(
          (generatedDailyStockByDefaultDayOfWeeks) => {
            return generatedDailyStockByDefaultDayOfWeeks.breadRef.id === bread.bread_ref?.id
          }
        )

        return (
          <React.Fragment key={bread.bread_ref.id}>
            {index !== 0 && <Spacer size={8} />}
            <SettingsConfirmCard
              breadName={getBreadShortName(bread)}
              expandStatus={expandStatus}
              imageUrl={bread.image_urls[0] || ''}
              resetExpandedStatus={resetExpandedStatus}
              dailySchedules={dailySchedules}
              preorderSchedules={preorderSchedules}
              prevDailySchedules={prevDailySchedules}
              prevPreorderSchedules={prevPreorderSchedules}
              stocks={stockList}
              temporaryOpenDates={temporaryOpenDatesForThisBread}
              generatedDailyStocksByDefaultDayOfWeeks={generatedDailyStocksByDefaultDayOfWeeksForThisBread}
            />
          </React.Fragment>
        )
      })}
    </>
  )
}

export default SettingsConfirmCardList
